import { useTranslation } from 'react-i18next'
import { Pressable, StyleSheet, View } from 'react-native'
import { Color, Font, Radius, Spacing, Text } from 'zens-storybook'

import type { ChatUsage } from './types'

interface Props {
  usage: ChatUsage | null
  error: boolean
  onRetry: () => void
}

const ChatPlan = ({ usage, error, onRetry }: Props) => {
  const { t } = useTranslation()
  const remaining = Math.max(0, Math.min(usage?.credits_remaining ?? 0, usage?.monthly_credits ?? 0))
  const total = usage?.monthly_credits ?? 0
  const label = usage?.bypass_reason === 'sysadmin' ? t('Unlimited') : usage?.plan === 'premium' ? t('Premium tier') : t('Free tier')
  const credits = t('{{remaining}} / {{total}} credits remaining', { remaining, total })

  return (
    <View style={styles.container} testID="AssistantChat.Plan">
      {usage ? (
        <>
          <View style={styles.badge}><Text style={styles.plan}>{label}</Text></View>
          {usage.plan === 'free' && !usage.unlimited && !error && total > 0 && (
            <View style={styles.balance}>
              <Text style={styles.caption}>{credits}</Text>
              <View
                style={styles.track}
                accessibilityRole="progressbar"
                accessibilityLabel={t('Remaining chatbot credits')}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={remaining}
                aria-valuetext={credits}
              >
                <View style={[styles.fill, { width: `${remaining / total * 100}%` }]} />
              </View>
            </View>
          )}
        </>
      ) : !error ? <Text style={styles.caption}>{t('Loading plan...')}</Text> : null}
      {error && (
        <Pressable onPress={onRetry} accessibilityRole="button" accessibilityLabel={t('Retry loading plan')}>
          <Text style={styles.error}>{t('Plan unavailable. Retry')}</Text>
        </Pressable>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: Spacing.S, flexWrap: 'wrap', flexShrink: 1 },
  badge: { borderRadius: Radius.S, backgroundColor: Color.sleepingGrey, paddingHorizontal: Spacing.S, paddingVertical: Spacing.XS },
  plan: { fontFamily: Font.regular, fontSize: 14, color: Color.graphiteGrey },
  balance: { width: 180, maxWidth: '100%', gap: Spacing.XS },
  caption: { fontFamily: Font.regular, fontSize: 12, color: Color.graphiteGrey },
  track: { height: 6, borderRadius: 3, backgroundColor: Color.calmGrey, overflow: 'hidden' },
  fill: { height: '100%', backgroundColor: Color.darkRefinedGreen, borderRadius: 3 },
  error: { fontFamily: Font.regular, fontSize: 12, color: Color.darkRed },
})

export default ChatPlan
